import {ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, signal, ViewChild} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatNativeDateModule} from '@angular/material/core';
import {RegistrationService} from '../services/registration.service';
import {ProjectService} from '../services/project.service';
import {Registration} from '../classes/registration';
import {Project} from '../classes/project';
import {CodaltComponent} from '../codalt.component';
import {IsdDatepickerComponent} from '../my-datepicker.component';
import {debounceTime, distinctUntilChanged, of, switchMap} from 'rxjs';
import {Title} from '@angular/platform-browser';
import {Utils} from '../utils.class';
import {AuthorisationService} from '../services/auth/authorisation.service';

@Component({
    selector: 'app-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['./registration.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatDatepickerModule,
        MatInputModule,
        MatFormFieldModule,
        MatNativeDateModule,
        DatePipe,
        IsdDatepickerComponent,
    ],
})
export class RegistrationComponent extends CodaltComponent implements OnInit {
    public currentTime = new Date();
    public startTime = this.getStartOfDay(new Date());

    @ViewChild('registrationhours') registrationhours: ElementRef;
    @ViewChild('comment') commentElement: ElementRef;

    searchNewProjectText = new FormControl<string>('');

    readonly projects = signal<Project[]>([]);
    readonly registrations = signal<Registration[]>([]);
    readonly searchNewProjects = signal<Project[]>([]);
    readonly quarters = signal<{ time: string }[]>([]);
    readonly hours = signal<Date[]>([]);
    readonly comments = signal<Record<string, string>>({});
    readonly saveButtonText = signal<string>('Save');
    readonly showErrorMessage = signal<boolean>(false);

    selectedDate: Date = new Date();
    commentProject: Project | null = null;
    commentQuarter: { time: string } | null = null;
    tempComment = new FormControl<string>('');
    reloadButtonText = 'Reload';
    lastMovedQuarter: Date | null = null;
    currentChangingProjectId: number | null = null;
    currentRegistering: boolean | null = null;
    isWorking = false;
    hoveredProject: number | null = null;
    showCommentModal = false;

    constructor(
        private registrationService: RegistrationService,
        private projectService: ProjectService,
        private ref: ChangeDetectorRef,
        private titleService: Title
    ) {
        super();
        this.titleService.setTitle(`Urenregistratie ${this.environment.titleAppend}`);
    }

    public getDifferenceInMinutes(date1: Date, date2: Date): number {
        return Math.floor((date1.getTime() - date2.getTime()) / 60000);
    }

    ngOnInit() {
        const updateCurrentTime = (context: RegistrationComponent) => {
            context.currentTime = new Date();

            // Calculate the offset so that current time + 1,3 hour is visible on screen
            let currentTimePosition = this.getDifferenceInMinutes(
                context.currentTime,
                context.startTime,
            );
            let registrationBlockWidth = context.registrationhours.nativeElement.clientWidth;

            if (currentTimePosition < 60 * 17 && currentTimePosition > 60 * 8) {
                currentTimePosition = 60 * 17;
            }

            // Set the scrollposition
            context.registrationhours.nativeElement.scrollLeft =
                currentTimePosition * 1.266666666 - registrationBlockWidth + 100;
        };

        setInterval(() => {
            updateCurrentTime(this);
        }, 60000);

        setTimeout(() => {
            updateCurrentTime(this);
        });

        this.subscriptions.add(
            this.searchNewProjectText.valueChanges
                .pipe(
                    debounceTime(150),
                    distinctUntilChanged(),
                    switchMap((value) => {
                        this.searchNewProjects.set([]);

                        if (value.length > 2) {
                            return this.projectService.findHiddenProject(value, this.selectedDate);
                        } else {
                            return of([]);
                        }
                    }),
                )
                .subscribe((value) => {
                    if (value.length > 0) {
                        this.searchNewProjects.set(value);
                    } else {
                        this.searchNewProjects.set([]);
                    }
                }),
        );

        let selectedDate = localStorage.getItem('selected_date');
        if (selectedDate) {
            this.selectedDateChanged(new Date(selectedDate));
        } else {
            this.loadRegistrations();
        }
    }

    public projectHover(projectId: number) {
        this.hoveredProject = projectId;
    }

    public projectLeave() {
        this.hoveredProject = null;
    }

    public loadRegistrations() {
        this.quarters.set([]);
        const hoursArray: Date[] = [];
        const startOfDay = this.getStartOfDay(this.selectedDate);

        for (let i: number = 0; i < 24 * 4; i++) {
            this.quarters.update((quarters) => [
                ...quarters,
                {time: this.addMinutes(startOfDay, i * 15).toISOString()},
            ]);
            if (i % 4 == 0) {
                hoursArray.push(this.addMinutes(startOfDay, i * 15));
            }
        }
        this.hours.set(hoursArray);

        this.projects.set([]);
        this.registrations.set([]);
        this.subscriptions.add(
            this.projectService.getVisible(this.selectedDate).subscribe((data) => {
                this.projects.set(data);
                this.reloadButtonText = 'Reload';
            }),
        );
        this.subscriptions.add(
            this.registrationService.getAll(this.selectedDate).subscribe((response) => {
                this.registrations.set(response.registrations || []);
                this.comments.set(response.comments || {});
                if (Array.isArray(this.comments())) {
                    this.comments.set({});
                }
            }),
        );
    }

    reload() {
        this.reloadButtonText = 'Loading...';
        this.projectService.azure().then(
            () => {
                this.loadRegistrations();
            },
            () => {
                this.loadRegistrations();
            },
        );
    }

    selectedDateChanged(event: Date) {
        this.selectedDate = event;
        this.selectedDate.setHours(10);

        if (this.isSameDay(this.selectedDate, new Date())) {
            localStorage.removeItem('selected_date');
        } else {
            const datePipe = new DatePipe('en-US');
            localStorage.setItem('selected_date', datePipe.transform(event, 'yyyy-MM-dd') || '');
        }

        const canEditByDate = Utils.canEditRegistrationDate(this.selectedDate, this.currentTime, AuthorisationService.hasRole(this.Roles.MutationPreviousPeriod), AuthorisationService.hasRole(this.Roles.MutationPrevPreviousPeriod));
        if (!canEditByDate) {
            this.showErrorMessage.set(true);
        } else {
            this.showErrorMessage.set(false);
        }

        this.loadRegistrations();
    }

    public save() {
        this.saveButtonText.set('saving...');
        this.subscriptions.add(
            this.registrationService
                .save(this.selectedDate, this.registrations(), this.comments())
                .subscribe(() => {
                    this.saveButtonText.set('saved!');
                    setTimeout(() => this.saveButtonText.set('save changes'), 1500);
                    this.loadRegistrations()
                }),
        );
    }

    public continue() {
        let latestOther: Registration | null = null;
        const now = new Date();

        this.registrations().forEach((registration) => {
            if (this.isBefore(new Date(registration.till), now)) {
                if (latestOther) {
                    // But when latestOther is set, the till should also be later than current latestOther
                    if (this.isBefore(new Date(latestOther.till), new Date(registration.till))) {
                        latestOther = registration;
                    }
                } else {
                    latestOther = registration;
                }
            }
        });

        if (latestOther) {
            const currentMinutes = now.getMinutes();
            let remainder = (currentMinutes % 15) * -1;
            if (remainder >= -7) {
                remainder = remainder - 15;
            }

            const nearest = new Date(now);
            nearest.setMinutes(currentMinutes + remainder);
            nearest.setSeconds(0);
            nearest.setMilliseconds(0);

            let event = new MouseEvent('mousemove', {shiftKey: true});

            this.register(nearest, latestOther.work_project_id, event);
        }
    }

    public addComment(quarter: any, project: any) {
        this.commentProject = project;
        this.commentQuarter = quarter;
        this.showCommentModal = true;

        let indexTime = new Date(this.commentQuarter.time).toString().substring(16, 21);
        this.tempComment.setValue(this.comments()[indexTime] || '');

        setTimeout(() => this.commentElement?.nativeElement.focus());
        return false;
    }

    public totalTime(projectId: number = null) {
        let time = 0;
        let projectRegistrations = projectId
            ? this.registrations().filter((p) => p.work_project_id == projectId)
            : this.registrations();

        projectRegistrations.forEach((reg) => {
            time += new Date(reg.till).getTime() - new Date(reg.from).getTime();
        });
        if (time > 0) {
            let minutes = time / 1000 / 60;
            return Math.floor(minutes / 60) + ':' + ('0' + (minutes % 60)).slice(-2);
        }
        return '';
    }

    public lg(var2) {
        let quarter = new Date(var2.target.dataset['quarter']);
        let workproject = var2.target.dataset['workproject'];
        if (var2.target.className.indexOf('quarter') == -1) {
            return false;
        }
        if (var2.type == 'mousedown') {
            this.currentChangingProjectId = +workproject;
            this.currentRegistering = !this.quarterSelected(this.currentChangingProjectId, quarter);
        }
        if (var2.type == 'mouseup') {
            this.lastMovedQuarter = null;
            this.currentChangingProjectId = null;
            this.currentRegistering = null;
        } else if (var2.buttons == 1 || var2.type == 'click') {
            if (this.lastMovedQuarter != quarter || var2.type == 'click') {
                this.lastMovedQuarter = quarter;
                this.register(quarter, this.currentChangingProjectId || workproject, var2);
            }
        }
    }

    public setComment() {
        if (!this.commentQuarter) return;

        let indexTime = new Date(this.commentQuarter.time).toString().substring(16, 21);
        if (this.tempComment.value === '') {
            delete this.comments()[indexTime];
        } else {
            this.comments()[indexTime] = this.tempComment.value;
        }
        this.showCommentModal = false;
    }

    clearComment() {
        this.tempComment.setValue('');
        this.setComment()
    }

    public register(quarter: any, project: any, event: MouseEvent) {
        if (this.isWorking) {
            return;
        }

        quarter = new Date(quarter);
        project = +project;

        const canEditByDate = Utils.canEditRegistrationDate(quarter, this.currentTime, AuthorisationService.hasRole(this.Roles.MutationPreviousPeriod), AuthorisationService.hasRole(this.Roles.MutationPrevPreviousPeriod));

        if (!canEditByDate) {
            this.showErrorMessage.set(true);
            return false
        }

        this.showErrorMessage.set(false);
        this.isWorking = true;

        let newRegistration = new Registration();
        newRegistration.work_project_id = project;
        newRegistration.from = quarter;
        newRegistration.till = this.addMinutes(quarter, 15);
        let impossible = false;

        // Loop through registrations and check whether there is already work registered this time
        this.registrations().forEach((registration) => {
            // Is this registration same or around (starting before, ending after) new quarter?
            if (
                this.isSameOrBefore(new Date(registration.from), newRegistration.from) &&
                this.isSameOrAfter(new Date(registration.till), newRegistration.till)
            ) {
                // If same project, unregister
                if (
                    this.currentRegistering === false &&
                    newRegistration.work_project_id == registration.work_project_id
                ) {
                    if (this.isSame(new Date(registration.from), newRegistration.from)) {
                        if (this.isSame(new Date(registration.till), newRegistration.till)) {
                            // from and till both same, simple splice
                            this.registrations().splice(
                                this.registrations().indexOf(registration),
                                1,
                            );
                        } else {
                            // From equal, till different
                            // Start the registration from the end of the clicked quarter
                            registration.from = newRegistration.till;
                        }
                    }
                    // From not equal, but till is equal?
                    else if (this.isSame(new Date(registration.till), newRegistration.till)) {
                        // Then end registration on the from moment of clicked quarter
                        registration.till = newRegistration.from;
                    } else {
                        // There is a registration around, but start or end is not matching clicked quarter

                        // If ctrlKey down, we have to remove the complete registration
                        if (event.ctrlKey) {
                            this.registrations().splice(
                                this.registrations().indexOf(registration),
                                1,
                            );
                        } else {
                            // Else we have to splice the registration in 2 registrations
                            let extraReg = new Registration();
                            extraReg.work_project_id = newRegistration.work_project_id;
                            extraReg.from = newRegistration.till;
                            extraReg.till = registration.till;
                            this.registrations().push(extraReg);

                            registration.till = newRegistration.from;
                        }
                    }
                }

                // Never register new times
                impossible = true;
            }
        });

        if (this.currentRegistering === false) {
            impossible = true;
        }

        // When a quarter was clicked which have already a registration
        // We never register new times, so return
        if (impossible) {
            this.isWorking = false;
            return false;
        }

        let extendedOther = false;

        // When shiftKey is pressed, we should extend the latest project
        if (event.shiftKey) {
            let latestOther = null;
            this.registrations().forEach((registration) => {
                // When till is before the start of new registration, we have a potential latestOther
                if (this.isSameOrBefore(new Date(registration.till), newRegistration.from)) {
                    if (latestOther) {
                        // But when latestOther is set, the till should also be later than current latestOther
                        if (
                            this.isSameOrBefore(
                                new Date(latestOther.till),
                                new Date(registration.till),
                            )
                        ) {
                            latestOther = registration;
                        }
                    } else {
                        latestOther = registration;
                    }
                }
            });
            if (latestOther) {
                if (latestOther.work_project_id == newRegistration.work_project_id) {
                    latestOther.till = newRegistration.till;
                    extendedOther = true;
                } else {
                    newRegistration.from = latestOther.till;
                }
            }
        } else {
            // Check wheter we could extend a current excisting registration
            this.registrations().forEach((registration) => {
                if (!extendedOther && registration.work_project_id == project) {
                    // Check if there is any other registration between registration.till and newRegistration.from
                    const hasRegistrationBetween = (from: Date, till: Date) =>
                        this.registrations().some((r) =>
                            r !== registration &&
                            this.isBefore(new Date(r.from), till) &&
                            this.isBefore(from, new Date(r.till)),
                        );

                    if (this.isSame(new Date(registration.from), newRegistration.till)) {
                        registration.from = newRegistration.from;
                        extendedOther = true;
                    } else if (
                        this.isSame(new Date(registration.till), newRegistration.from) &&
                        !hasRegistrationBetween(new Date(registration.till), newRegistration.till)
                    ) {
                        registration.till = newRegistration.till;
                        extendedOther = true;
                    } else if (
                        event.type == 'mousemove' &&
                        this.isBefore(new Date(registration.till), newRegistration.from) &&
                        this.getDifferenceInMinutes(newRegistration.from, new Date(registration.till)) <= 30 &&
                        !hasRegistrationBetween(new Date(registration.till), newRegistration.till)
                    ) {
                        registration.till = newRegistration.till;
                        extendedOther = true;
                    }
                }
            });
        }

        // When we didn't extend another registration, push the new registration object
        if (!extendedOther) this.registrations().push(newRegistration);

        // Check wheter we could simplify the registration
        // This is the case when we register subsequent registrations for same projects
        let lastRegistration = null;
        this.registrations()
            .sort((a, b) => {
                return new Date(a.from).getTime() - new Date(b.from).getTime();
            })
            .forEach((registration) => {
                if (
                    lastRegistration &&
                    this.isSame(new Date(lastRegistration.till), new Date(registration.from)) &&
                    lastRegistration.work_project_id == registration.work_project_id
                ) {
                    lastRegistration.till = registration.till;
                    this.registrations().splice(this.registrations().indexOf(registration), 1);
                }

                lastRegistration = registration;
            });

        this.ref.detectChanges();

        this.isWorking = false;
    }

    public quarterIsWholeHour(quarter) {
        return new Date(quarter).getMinutes() === 0;
    }

    public officeTime(quarter) {
        let hour = new Date(quarter).getHours();
        return hour > 7 && hour < 17;
    }

    public hasComment(time) {
        let indexTime = new Date(time).toString().substring(16, 21);
        return this.comments().hasOwnProperty(indexTime);
    }

    public getCommentsArray(): { key: string; value: string }[] {
        return Object.entries(this.comments()).map(([key, value]) => ({key, value}));
    }

    public isToday(date: Date) {
        return this.isSameDay(date, new Date());
    }

    public earliestEditableDate(): Date {
        return Utils.earliestEditableDate(this.currentTime, AuthorisationService.hasRole(this.Roles.MutationPreviousPeriod), AuthorisationService.hasRole(this.Roles.MutationPrevPreviousPeriod));
    }

    public isFuture(date: Date) {
        return this.getStartOfDay(date) > this.getStartOfDay(new Date());
    }

    public isWeekend(date: Date) {
        const day = date.getDay();
        return day === 0 || day === 6; // Sunday = 0, Saturday = 6
    }

    public today() {
        this.selectedDateChanged(new Date());
    }

    public removeVisible(project: Project) {
        this.searchNewProjects.set([]);
        this.searchNewProjectText.setValue('');
        this.projectService.toggleVisible(project, false).then(() => {
            this.loadRegistrations();
        });
    }

    public addVisible(project: Project) {
        this.searchNewProjects.set([]);
        this.searchNewProjectText.setValue('');
        this.projectService.toggleVisible(project, true).then(() => {
            this.loadRegistrations();
        });
    }

    public quarterSelected(project, quarter) {
        let quarterTime = new Date(quarter).getTime();

        return (
            this.registrations()
                .filter((p) => p.work_project_id === project)
                .filter(
                    (registration) =>
                        new Date(registration.from).getTime() <= quarterTime &&
                        new Date(registration.till).getTime() > quarterTime,
                ).length > 0
        );
    }

    // Helper methods for date manipulation
    private getStartOfDay(date: Date): Date {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    }

    private addMinutes(date: Date, minutes: number): Date {
        return new Date(date.getTime() + minutes * 60000);
    }

    private isSameDay(date1: Date, date2: Date): boolean {
        return (
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
        );
    }

    private isSameOrBefore(date1: Date, date2: Date): boolean {
        return date1.getTime() <= date2.getTime();
    }

    private isSameOrAfter(date1: Date, date2: Date): boolean {
        return date1.getTime() >= date2.getTime();
    }

    private isSame(date1: Date, date2: Date): boolean {
        return date1.getTime() === date2.getTime();
    }

    private isBefore(date1: Date, date2: Date): boolean {
        return date1.getTime() < date2.getTime();
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.showCommentModal = false;
        }

        if (event.ctrlKey === true && event.key === "s") {
            event.preventDefault()
            if (this.showCommentModal) {
                this.setComment();
            } else {
                this.save();
            }
        }
    }
}
